// `EvidencePhotos` — the attachment strip (design-system spec §3.9, the honest
// reference states, extended to attachments).
//
// A record that carries `photo_file_ids` used to render THE COUNT: "Photos 1".
// A count is not evidence. This is the primitive that shows the pictures, and
// it is a shared component rather than a waste screen's helper because three
// modules already attach files under the same three-step contract — module 04's
// defect flag (`production_evidence`), module 05's waste capture and remnant
// intake (`waste_evidence`), module 07's quality records (`quality_evidence`).
// A photo strip written inside one area is a photo strip the next two hand-roll.
//
// FOUR STATES, all of them said out loud (the `RefLabel` rule, applied to bytes
// instead of names):
//   NONE      — the record has no attachments. The caller renders nothing at
//               all; there is no empty frame to misread as a failed image.
//   RESOLVING — the presign is in flight. A reserving placeholder, so the
//               drawer does not jump when three thumbnails land.
//   READY     — the thumbnail, linked to the full-size presigned URL.
//   FAILED    — the id resolved to nothing readable (a `rejected` upload whose
//               digest disagreed, or a file this caller may not open). Said in
//               words: a broken `<img>` glyph is not a message.
//
// The link opens in a new tab: the presigned URL is short-lived and navigating
// the SPA away to it would lose the ledger position and strand the user on a
// URL that expires. `rel="noreferrer"` keeps the signed URL out of any referrer
// header.

import { shortId } from "../lib/short-id";
import { useTranslation } from "react-i18next";
import { isViewableImage, useFileDownloads } from "../lib/files";

export interface EvidencePhotosProps {
  /** The record's attachment ids, in the order they were captured. */
  fileIds: readonly string[];
  /** Test hook for the strip as a whole. */
  "data-testid"?: string;
}

export function EvidencePhotos({ fileIds, "data-testid": testId }: EvidencePhotosProps) {
  const { t } = useTranslation();
  const files = useFileDownloads(fileIds);

  if (fileIds.length === 0) {
    return (
      <span className="ref-none" title={t("evidence.none")}>
        —
      </span>
    );
  }

  return (
    <ul className="ds-evidence" data-testid={testId ?? "evidence-photos"}>
      {files.map((file, index) => {
        const caption = t("evidence.caption", { n: index + 1, total: files.length });
        if (file.isPending) {
          return (
            <li key={file.id} className="ds-evidence-item ds-evidence-pending">
              <span className="ds-evidence-frame" aria-label={t("evidence.resolving")} />
            </li>
          );
        }
        if (file.isFailed || !isViewableImage(file.record) || file.url === undefined) {
          return (
            <li key={file.id} className="ds-evidence-item ds-evidence-failed">
              <span className="ds-evidence-note" data-testid="evidence-unavailable">
                {t("evidence.unavailable")}
              </span>
              <code className="ref-id">{shortId(file.id)}</code>
            </li>
          );
        }
        return (
          <li key={file.id} className="ds-evidence-item">
            <a href={file.url} target="_blank" rel="noreferrer" title={t("evidence.open")}>
              <img className="ds-evidence-frame" src={file.url} alt={caption} loading="lazy" />
            </a>
          </li>
        );
      })}
    </ul>
  );
}
